import { Payment, Transaction } from './../interfaces/transactions';
import { Course, Lesson, Session } from 'src/app/interfaces/course';
import { HttpClient, HttpRequest, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { HomepageSettings } from '../interfaces/settings';

export enum HttpMethod{
  GET = 1,
  POST = 2,
  PUT = 3,
  DELETE = 4
}

interface HttpOptions{
  orderBy?: string
}

interface HttpResponse{
  status: number;
  message: string;
  data: any;
}

@Injectable({
  providedIn: 'root'
})

export class DataService {
  // private baseHref = 'http://localhost:4000';
  private baseHref = 'https://bilinstudio.maksymblank.com';

  private token: string;
  private privateToken: string;

  constructor(
    private http: HttpClient,
    private toastr: ToastrService
  ) {
  }

  setToken(token, privateToken = false){
    if(!privateToken){
      localStorage.setItem('token', token);
      this.token = token;
    }else{
      this.privateToken = token;
    }
  }

  request(method: HttpMethod, url: string, body: any = null, options: any = {}){
    let headers = new HttpHeaders();

    headers = headers.append('Content-Type', 'application/json');

    this.token = this.token || localStorage.getItem('token');

    if(this.token){
      headers = headers.append('Authorization', `Bearer ${this.token}`);
    }else if(this.privateToken && url === '/auth/update'){
      headers = headers.append('Authorization', `Bearer ${this.privateToken}`);
    }


    url = this.baseHref + url;

    let params = new HttpParams();

    for(let key in options){
      params = params.append(key, options[key]);
    }

    switch(method){
      case HttpMethod.GET: {
        return this.http.get(url, {
          headers: headers,
          params: params
        }).pipe(
          tap((res: HttpResponse) => {
            if(res && res.status === 200){
              if(res.message){
                this.toastr.success(res.message);
              }
            }
          }),
          catchError(e => {
            this.toastr.error(e.error.message || 'Oops.. Something went wrong.');

            return of(null);
          })
        )
      }

      case HttpMethod.POST: {
        return this.http.post(url, body, {
          headers: headers,
          params: params
        }).pipe(
          tap((res: HttpResponse) => {
            if(res && res.status === 200){
              if(res.message){
                this.toastr.success(res.message);
              }
            }
          }),
          catchError((e) => {
            this.toastr.error(e.error.message || 'Oops.. Something went wrong.');

            return of(null);
          })
        )
      }

      case HttpMethod.PUT: {
        return this.http.put(url, body, {
          headers: headers,
          params: params
        }).pipe(
          tap((res: HttpResponse) => {
            if(res && res.status === 200){
              if(res.message){
                this.toastr.success(res.message);
              }
            }
          }),
          catchError((e) => {
            this.toastr.error(e.error.message || 'Oops.. Something went wrong.');

            return of(null);
          })
        )
      }

      case HttpMethod.DELETE: {
        return this.http.delete(url, {
          headers: headers,
          params: params
        }).pipe(
          tap((res: HttpResponse) => {
            if(res && res.status === 200){
              if(res.message){
                this.toastr.success(res.message);
              }
            }
          }),
          catchError((e) => {
            this.toastr.error(e.error.message || 'Oops.. Something went wrong.');

            return of(null);
          })
        )
      }
    }
  }

  getCourses(options: HttpOptions = {}){
    return this.request(HttpMethod.GET, '/courses', null, options);
  }

  getCourse(course_id: string){
    return this.request(HttpMethod.GET, `/courses/${course_id}`);
  }

  addCourse(data: Course){
    return this.request(HttpMethod.POST, '/courses', data);
  }

  updateCourse(course_id: string, data: Course){
    return this.request(HttpMethod.PUT, `/courses/${course_id}`, data);
  }

  deleteCourse(course_id: string){
    return this.request(HttpMethod.DELETE, `/courses/${course_id}`);
  }

  getAllPromotions(){
    return this.request(HttpMethod.GET, `/courses/promotions`);
  }

  getFeatured(){
    return this.request(HttpMethod.GET, `/courses/featured`);
  }

  getLessons(course_id: string = '', params = {}){
    return this.request(HttpMethod.GET, `/courses/${course_id ? `${course_id}/` : ''}lessons`, null, params);
  }

  getLesson(course_id: string, lesson_id: string){
    return this.request(HttpMethod.GET, `/courses/${course_id}/lessons/${lesson_id}`);
  }

  addLesson(course_id: string, data: Lesson){
    return this.request(HttpMethod.POST, `/courses/${course_id}/lessons`, data);
  }

  updateLesson(course_id: string, lesson_id: string, data: Lesson){
    return this.request(HttpMethod.PUT, `/courses/${course_id}/lessons/${lesson_id}`, data);
  }

  deleteLesson(course_id: string, lesson_id: string){
    return this.request(HttpMethod.DELETE, `/courses/${course_id}/lessons/${lesson_id}`);
  }

  getLessonSession(course_id: string, lesson_id: string){
    return this.request(HttpMethod.GET, `/courses/${course_id}/lessons/${lesson_id}/sessions`);
  }

  addLessonSession(course_id: string, lesson_id: string, data: Session[]){
    return this.request(HttpMethod.POST, `/courses/${course_id}/lessons/${lesson_id}/sessions`, {sessions: data});
  }

  updateLessonSession(course_id: string, lesson_id: string, session_id: string, data: Session){
    return this.request(HttpMethod.PUT, `/courses/${course_id}/lessons/${lesson_id}/sessions/${session_id}`, data);
  }

  deleteLessonSession(course_id: string, lesson_id: string, session_id: string){
    return this.request(HttpMethod.DELETE, `/courses/${course_id}/lessons/${lesson_id}/sessions/${session_id}`);
  }

  getEnrollments(params: any = {}){
    return this.request(HttpMethod.GET, `/enrollments`, null, params);
  }

  getMyEnrollments(){
    return this.request(HttpMethod.GET, `/my/enrollments`);
  }

  addEnrollments(data: any){
    return this.request(HttpMethod.POST, `/enrollments`, data);
  }

  updateEnrollment(id: string, data: any){
    return this.request(HttpMethod.PUT, `/enrollments/${id}`, data);
  }

  deleteEnrollment(id: string){
    return this.request(HttpMethod.DELETE, `/enrollments/${id}`);
  }

  updateUser(data){
    return this.request(HttpMethod.PUT, `/auth/update`, data);
  }

  getUsers(params = {}){
    return this.request(HttpMethod.GET, `/users`, null, params);
  }

  getUser(id: string){
    return this.request(HttpMethod.GET, `/users/${id}`);
  }

  deleteUser(id: string){
    return this.request(HttpMethod.DELETE, `/users/${id}`);
  }

  getTeachers(params = {}){
    return this.request(HttpMethod.GET, `/users/teachers`, null, params);
  }

  addTeacher(teacher: any){
    return this.request(HttpMethod.POST, `/users/teachers`, teacher);
  }

  approveTeacher(teacher_id: string){
    return this.request(HttpMethod.PUT, `/users/teachers/${teacher_id}/approve`);
  }

  getSettings(type: string){
    return this.request(HttpMethod.GET, `/settings/${type}`);
  }

  updateSettings(type: string, data: HomepageSettings){
    return this.request(HttpMethod.PUT, `/settings/${type}`, data);
  }

  presignS3File(key: string, contentType: string){
    return this.request(HttpMethod.POST, `/file-uploader`, {
      key: key,
      contentType: contentType
    })
  }

  uploadS3File(url: string, file: File){
    console.log(file);

    return this.http.put(url, file, {
      headers: new HttpHeaders({
        'Content-Type': file.type
      })
    });
  }

  getDrawings(params = {}){
    return this.request(HttpMethod.GET, `/assets/drawings`, null, params);
  }

  addDrawing(data: any){
    return this.request(HttpMethod.POST, `/assets/drawings`, data);
  }

  deleteDrawing(id: string){
    return this.request(HttpMethod.DELETE, `/assets/drawings/${id}`);
  }

  getMaterials(params = {}){
    return this.request(HttpMethod.GET, `/assets/materials`, null, params);
  }

  addMaterial(data: any){
    return this.request(HttpMethod.POST, `/assets/materials`, data);
  }

  deleteMaterial(id: string){
    return this.request(HttpMethod.DELETE, `/assets/materials/${id}`);
  }

  getReviews(params = {}){
    return this.request(HttpMethod.GET, `/reviews`, null, params);
  }

  addReview(data: any){
    return this.request(HttpMethod.POST, `/reviews`, data);
  }

  deleteReview(id: string){
    return this.request(HttpMethod.DELETE, `/reviews/${id}`);
  }

  getMyTransactions(){
    return this.request(HttpMethod.GET, `/my/transactions`);
  }

  getProfile(){
    return this.request(HttpMethod.GET, '/my/profile');
  }

  updateProfile(data: any){
    return this.request(HttpMethod.PUT, `/my/profile`, data);
  }

  getMyPayments(){
    return this.request(HttpMethod.GET, `/my/payments`);
  }

  addPayment(data: Payment){
    return this.request(HttpMethod.POST, `/payments/`, data);
  }

  getComments(to: string){
    return this.request(HttpMethod.GET, `/comments`, null, {to: to});
  }

  addComment(data: any){
    return this.request(HttpMethod.POST, `/comments`, data);
  }

  deleteComment(id: string){
    return this.request(HttpMethod.DELETE, `/comments/${id}`);
  }

  getAllReviews(){
    return this.request(HttpMethod.GET, `/admin/reviews`);
  }

  getAllComments(){
    return this.request(HttpMethod.GET, `/admin/comments`);
  }

  getAllPayments(){
    return this.request(HttpMethod.GET, `/admin/payments`);
  }

  getAllTransactions(){
    return this.request(HttpMethod.GET, `/admin/transactions`);
  }

  addTransaction(body: Transaction){
    return this.request(HttpMethod.POST, `/admin/transactions`, body);
  }

  addAdminEnrollments(body: any){
    return this.request(HttpMethod.POST, `/admin/enrollments`, body);
  }

  updateAdminUser(uid: string, body: any){
    return this.request(HttpMethod.PUT, `/admin/users/${uid}`, body);
  }

  addAdmin(body: any){
    return this.request(HttpMethod.POST, `/admin`, body);
  }

  requestZoomId(body: {userid: string}){
    return this.request(HttpMethod.POST, `/zoom`, body);
  }

  deleteZoomId(id: string){
    return this.request(HttpMethod.DELETE, `/zoom/${id}`);
  }

  createMeeting(body: any){
    return this.request(HttpMethod.POST, `/zoom/meeting`, body);
  }

  checkSessionCompetion(body: any){
    return this.request(HttpMethod.POST, `/zoom/completion`, body);
  }

  completeLessonSession(course_id: string, lesson_id: string, session_id: string){
    return this.request(HttpMethod.POST, `/courses/${course_id}/lessons/${lesson_id}/sessions/${session_id}/complete`);
  }

  cancelLessonSession(course_id: string, lesson_id: string, session_id: string){
    return this.request(HttpMethod.POST, `/courses/${course_id}/lessons/${lesson_id}/sessions/${session_id}/cancel`);
  }

  rescheduleLessonSession(course_id: string, lesson_id: string, session_id: string){
    return this.request(HttpMethod.POST, `/courses/${course_id}/lessons/${lesson_id}/sessions/${session_id}/reschedule`);
  }

  getUpcomingLesson(){
    return this.request(HttpMethod.GET, `/courses/lessons/upcoming`);
  }
}
